import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import TransactionDialog, {
  TransactionDialogTrigger,
} from "@/components/shared/transaction-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { PageBody } from "@/components/ui/layout"
import { EducatorsClassesClassesShow } from "@/types/serializers"
import { ArrowDownLeft, ArrowLeft, ArrowUpRight, Inbox } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, usePathParam, useProps } from "@/lib/page-rendering"

interface PageProps {
  class_priorities: Array<EducatorsClassesClassesShow>
  class_name: string
}

export default function Index() {
  const { class_priorities, class_name } = useProps<PageProps>()
  const { t } = useTranslation(["go_live", "common"])
  const teacherId = usePathParam("teacherId")
  const classesHref = `/educators/teachers/${teacherId}/classes`

  return (
    <>
      <EducatorPageChrome records={{ "educators/classes/edit": class_name }} />
      <PageBody>
        {class_priorities.length === 0 ? (
          <Card>
            <Empty>
              <EmptyHeader>
                <EmptyMedia
                  variant="icon"
                  className="text-muted size-20 bg-white"
                >
                  <Inbox className="size-16" />
                </EmptyMedia>
                <EmptyTitle>{t("show.empty_list_title")}</EmptyTitle>
                <EmptyDescription>
                  {t("show.empty_list_description")}
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Link href={classesHref} className={buttonVariants()}>
                  <ArrowLeft />
                  {t("common:actions.back")}
                </Link>
              </EmptyContent>
            </Empty>
          </Card>
        ) : (
          <ol className="space-y-4">
            {class_priorities.flatMap((classPriority) =>
              classPriority.class_priority_instances.map((instance) => (
                <GoLiveCard
                  {...classPriority}
                  {...instance}
                  instanceCount={classPriority.class_priority_instances.length}
                  key={`${classPriority.id}:${instance.instance_index}`}
                />
              )),
            )}
          </ol>
        )}
      </PageBody>
    </>
  )
}

function GoLiveCard({
  id,
  live,
  class_name,
  maximum,
  minimum,
  num_students_allocated,
  rank,
  listing_profile_name,
  tams_run_id,
  tams_run_teacher_input,
  requests_to_leave_count,
  requests_to_join_count,
  instanceCount,
  instance_index,
}: EducatorsClassesClassesShow &
  EducatorsClassesClassesShow["class_priority_instances"][number] & {
    instanceCount: number
  }) {
  const { t } = useTranslation(["go_live", "common", "classes"])
  const capacityPercentage = (num_students_allocated / maximum) * 100
  const minimumPercentage = (minimum / maximum) * 100
  const isBelowMinimum = num_students_allocated < minimum

  const minimumEqualsMaximum = minimum === maximum

  return (
    <Card role="listitem">
      <CardHeader className="flex flex-row justify-between pb-0">
        <div className="grid grid-cols-1 gap-2">
          <CardTitle>
            {class_name}
            {instanceCount > 1 && (
              <span>
                {" "}
                ({instance_index + 1}/{instanceCount})
              </span>
            )}
          </CardTitle>
          <span className="text-muted-foreground">
            {listing_profile_name} -{" "}
            <span data-testid="priority-rank">
              {t("show.priority_rank", { rank })}
            </span>
          </span>
        </div>
        <Badge
          data-testid="status-badge"
          data-live={live}
          className="data-[live=true]:bg-brand-wise-green data-[live=false]:bg-brand-philo-mustard h-fit rounded-full px-4 text-base"
        >
          {t("show.status", {
            context: live ? "true" : "false",
          })}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="flex w-full flex-row items-center gap-8">
          <div className="grow space-y-2">
            <div className="space-y-1">
              <span
                className="text-foreground font-medium"
                data-testid="allocated-description"
              >
                {t("show.allocated", { count: num_students_allocated })}
              </span>
              <div className="bg-muted relative h-2 w-full overflow-hidden rounded-full">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    isBelowMinimum ? "bg-destructive" : "bg-green-500"
                  }`}
                  style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
                />

                <div
                  className="pointer-events-none absolute top-0 h-full w-1 bg-black"
                  style={{
                    left: `${minimumPercentage}%`,
                    transform: "translateX(-50%)",
                  }}
                />
              </div>

              <div className="relative mt-1 h-6 w-full">
                {minimumEqualsMaximum ? (
                  <span
                    className="text-muted-foreground absolute text-xs whitespace-nowrap"
                    style={{ right: 0 }}
                  >
                    {t("show.capacity", { maximum })}
                  </span>
                ) : (
                  <>
                    <span
                      className="text-muted-foreground absolute text-xs whitespace-nowrap"
                      style={{
                        left: `${minimumPercentage}%`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      {t("show.minimum_label", { minimum })}
                    </span>
                    <span
                      className="text-muted-foreground absolute text-xs whitespace-nowrap"
                      style={{ right: 0 }}
                    >
                      {t("show.maximum_label", { maximum })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <TransactionDialog
            route={`./go_live`}
            options={{
              method: "post",
              data: { tams_run_id, class_priority_id: id, instance_index },
            }}
            initDescription={t("show.go_live.init_description")}
            pendingDescription={t("show.go_live.pending_description")}
            successDescription={t("show.go_live.success_description")}
            errorDescription={t("show.go_live.error_description")}
          >
            <TransactionDialogTrigger>
              <Button
                className="col-span-2 w-min justify-self-end-safe"
                disabled={live || !tams_run_teacher_input}
              >
                {t("show.actions.go_live")}
              </Button>
            </TransactionDialogTrigger>
          </TransactionDialog>
        </div>
      </CardContent>
      <CardFooter className="border-border flex items-center justify-between gap-4 border-t pt-1 text-sm">
        <div className="flex items-center gap-6">
          <div>
            <span>{t("show.allocated_label")}</span>
            <span className="text-foreground ml-1 font-semibold">
              {num_students_allocated}
            </span>
          </div>
          <div>
            <span>{t("show.minimum_footer_label")}</span>
            <span className="ml-1 font-semibold">{minimum}</span>
          </div>
          <div>
            <span>{t("show.maximum_footer_label")}</span>
            <span className="ml-1 font-semibold">{maximum}</span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div>
            {requests_to_leave_count !== null ? (
              <span
                className="inline-flex items-center gap-1 [&_svg]:size-5"
                data-testid="request-to-leave-count"
              >
                <ArrowUpRight />
                {t("show.requests_to_leave", {
                  count: requests_to_leave_count,
                })}
              </span>
            ) : null}
          </div>
          <div>
            {requests_to_join_count !== null ? (
              <span
                className="inline-flex items-center gap-1 [&_svg]:size-5"
                data-testid="request-to-join-count"
              >
                <ArrowDownLeft />
                {t("show.requests_to_join", { count: requests_to_join_count })}
              </span>
            ) : null}
          </div>
        </div>
      </CardFooter>
    </Card>
  )
}
